import React, { useState, useEffect } from 'react';
import { acceptInvite, getGuestData, checkInviteCode, declineInvite, validateAddress } from '../api';

const Invitation = () => {
    // State to hold the fetched guest data
    const [guestData, setGuestData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState('verify'); // 'verify', 'view', 'form', 'submitted', 'error'
    const [guestEmail, setGuestEmail] = useState('');
    const [inviteCode, setInviteCode] = useState('');
    const [verifying, setVerifying] = useState(false);

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        address: '',
        mealChoice: 'Chicken',
        allergies: ''
    });
    const [error, setError] = useState(null);

    // Address Validation State
    const [validationState, setValidationState] = useState(null); // 'FIX', 'CONFIRM_ADD_SUBPREMISES', 'CONFIRM'
    const [suggestedAddress, setSuggestedAddress] = useState('');
    const [isAddressValidated, setIsAddressValidated] = useState(false);
    const [declineConfirmation, setDeclineConfirmation] = useState(false);


    // Optional initial setup or check id if needed in future
    useEffect(() => {
        // No longer expecting guestId in URL query
    }, []);




    const handleVerify = async (e) => {
        e.preventDefault();
        if (!guestEmail || !inviteCode) {
            setError("Email and invite code are required.");
            return;
        }

        setVerifying(true);
        setError(null);
        const cleanEmail = guestEmail.trim().toLowerCase();
        const cleanCode = inviteCode.trim();
        try {
            // Check code against backend
            // If checkInviteCode resolves, it means the code is valid (200 OK)
            const verifyRes = await checkInviteCode(cleanEmail, cleanCode);
            const verifiedGuestId = verifyRes.guestId;

            // Code verified, now fetch the data
            const data = await getGuestData(verifiedGuestId);
            if (data) {
                setGuestData(data);

                // Pre-fill form from existing data if possible, or name
                const savedForm = data.form_data || {};
                const names = (data.name || '').split(' ');

                setFormData(prev => ({
                    ...prev,
                    firstName: savedForm.firstName || names[0] || '',
                    lastName: savedForm.lastName || names.slice(1).join(' ') || '',
                    address: savedForm.address || '',
                    mealChoice: savedForm.mealChoice || 'Chicken',
                    allergies: savedForm.allergies || ''
                }));

                if (data.status === 'Accepted') {
                    setStatus('already_accepted');
                } else {
                    setStatus('view');
                }
            } else {
                setError('Verified, but failed to load invitation details.');
            }

        } catch (err) {
            console.error(err);
            if (err.status === 400) {
                setError('Invalid invite code. Please check your code and try again.');
            } else {
                setError('Error verifying code. Please try again.');
            }
        } finally {
            setVerifying(false);
        }
    };

    const showForm = () => {
        setStatus('form');
    };

    const handleDecline = () => {
        setDeclineConfirmation(true);
    };

    const handleConfirmDecline = async () => {
        setDeclineConfirmation(false);
        setLoading(true);
        try {
            await declineInvite(guestData.id);
            setStatus('declined');
        } catch (err) {
            console.error(err);
            setError("Failed to decline invitation. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleAccept = async (e) => {
        e.preventDefault();

        // 1. Enforce Address Validation
        if (!isAddressValidated) {
            setLoading(true); // temporary loading for validation
            const isValid = await handleAddressValidation();
            setLoading(false);

            if (!isValid) return; // Stop submission if validation failed or needs user interaction
        }

        setLoading(true);
        try {
            // Combine first and last name for the payload or send as is? 
            // The prompt asks for fields: First Name, Last Name. 
            // Backend might expect "name". 
            // I'll send both or combined "name".
            const combinedName = `${formData.firstName} ${formData.lastName}`.trim();
            const payload = {
                name: combinedName,
                address: formData.address,
                mealChoice: formData.mealChoice,
                allergies: formData.allergies,
                // Include other form data if needed by backend, e.g. email
                email: guestData.email
            };

            await acceptInvite(guestData.id, payload);
            setStatus('submitted');
        } catch (err) {
            console.error(err);
            setError("Failed to accept invitation. Please try again.");
        } finally {
            setLoading(false);
        }
    };


    const determineResolutionAction = (result) => {
        if (!result || !result.result || !result.result.verdict) return 'ACCEPT';
        const { verdict, address } = result.result;

        // Logic based on Google Address Validation API response patterns

        // 1. FIX: granularity is too coarse OR unconfirmed components
        if (verdict.validationGranularity === 'OTHER' || verdict.validationGranularity === 'ROUTE') {
            return 'FIX';
        }
        if (verdict.hasUnconfirmedComponents) {
            return 'FIX';
        }

        // 2. CONFIRM_ADD_SUBPREMISES: Missing unit number
        const missingComponents = address.missingComponentTypes || [];
        if (missingComponents.includes('sub_premise')) {
            return 'CONFIRM_ADD_SUBPREMISES';
        }

        // 3. CONFIRM: Replaced or Inferred components (significant changes)
        // If the address is complete but changed, we ask the user to confirm.
        if (verdict.hasReplacedComponents || verdict.hasInferredComponents) {
            return 'CONFIRM';
        }

        // 4. Continue
        return 'ACCEPT';
    };

    const handleAddressValidation = async () => {
        if (!formData.address) return false;

        // Try to validate
        try {
            const result = await validateAddress(formData.address);
            console.log("Address Validation Result:", result);

            const action = determineResolutionAction(result);
            const formattedAddress = result?.result?.address?.formattedAddress;

            if (action === 'ACCEPT') {
                if (formattedAddress) {
                    setFormData(prev => ({ ...prev, address: formattedAddress }));
                }
                setIsAddressValidated(true);
                return true; // Validated successfully
            } else {
                // Must show modal
                setSuggestedAddress(formattedAddress || formData.address);
                setValidationState(action);
                return false; // Not yet validated (user needs to confirm)
            }

        } catch (err) {
            console.error("Address validation failed", err);
            // On error, do NOT automatically allow. Force user to review/confirm in modal.
            // treating API failures as "we couldn't verify this".
            setValidationState('FIX');
            return false;
        }
    };

    const handleValidationConfirm = () => {
        // User accepts the suggested address
        setFormData(prev => ({ ...prev, address: suggestedAddress }));
        setValidationState(null);
        setIsAddressValidated(true);
    };

    const handleValidationKeep = () => {
        // User keeps their original input despite warning
        setValidationState(null);
        setIsAddressValidated(true);
    };

    const handleValidationEdit = () => {
        // User wants to fix it manually
        setValidationState(null);
        // Focus is already likely lost, but user can click back in.
    };

    const handleDownloadCalendar = () => {
        const icsContent = [
            'BEGIN:VCALENDAR',
            'VERSION:2.0',
            'BEGIN:VTIMEZONE',
            'TZID:America/Los_Angeles',
            'BEGIN:DAYLIGHT',
            'TZOFFSETFROM:-0800',
            'TZOFFSETTO:-0700',
            'TZNAME:PDT',
            'DTSTART:19700308T020000',
            'RRULE:FREQ=YEARLY;BYMONTH=3;BYDAY=2SU',
            'END:DAYLIGHT',
            'BEGIN:STANDARD',
            'TZOFFSETFROM:-0700',
            'TZOFFSETTO:-0800',
            'TZNAME:PST',
            'DTSTART:19701101T020000',
            'RRULE:FREQ=YEARLY;BYMONTH=11;BYDAY=1SU',
            'END:STANDARD',
            'END:VTIMEZONE',
            'BEGIN:VEVENT',
            'DTSTART;TZID=America/Los_Angeles:20260429T113000',
            'DTEND;TZID=America/Los_Angeles:20260429T130000',
            'SUMMARY:Raising Girls 3rd Annual Luncheon',
            'DESCRIPTION:Join us for the 3rd Annual Luncheon of Raising Girls on April 29th at Hotel Murano Pavilion.',
            'LOCATION:Hotel Murano Pavilion',
            'END:VEVENT',
            'END:VCALENDAR'
        ].join('\n');

        const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'RaisingGirlsLuncheon.ics');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };


    return (
        <div className="invitation-container">
            <div className="invitation-card">
                {status === 'verify' && <h2>Invitation</h2>}

                {loading && <div className="loading-bar"></div>}

                {status === 'verify' && !loading && (
                    <form onSubmit={handleVerify} className="verify-form">
                        <div style={{ padding: '1rem', backgroundColor: '#fffbeb', color: '#92400e', borderRadius: '0.375rem', marginBottom: '1.5rem', border: '1px solid #fcd34d', textAlign: 'center' }}>
                            The RSVP deadline has passed. Please reach out to <a href="mailto:info@raisinggirls.org" style={{ color: '#92400e', fontWeight: 'bold', textDecoration: 'underline' }}>info@raisinggirls.org</a> for any questions. Thank you!
                        </div>
                        <h3>Access Your Invitation</h3>
                        <p style={{ marginBottom: '1rem', color: '#64748b' }}>Please enter the exact email address that received the invitation, along with your access code.</p>
                        <div className="form-group">
                            <input
                                type="email"
                                value={guestEmail}
                                onChange={e => setGuestEmail(e.target.value)}
                                placeholder="Email Address"
                                required
                                disabled
                            />
                        </div>
                        <div className="form-group">
                            <input
                                type="text"
                                value={inviteCode}
                                onChange={e => setInviteCode(e.target.value)}
                                placeholder="Enter Code"
                                required
                                disabled
                            />
                        </div>
                        {error && <p className="error-text">{error}</p>}
                        <button type="submit" className="submit-button" disabled={verifying}>
                            {verifying ? 'Verifying...' : 'Access Invitation'}
                        </button>
                    </form>
                )}

                {status === 'already_accepted' && guestData && (
                    <div className="invitation-view">
                        <div className="welcome-header">
                            <h2>You're Registered!</h2>
                            <p className="welcome-message">
                                You have already accepted this invitation. We look forward to seeing you!
                            </p>
                        </div>

                        <div className="invitation-actions">
                            <button onClick={showForm} className="accept-btn">Update Details</button>
                            <button onClick={handleDecline} className="decline-btn">I can no longer make it</button>
                        </div>
                    </div>
                )}

                {status === 'view' && guestData && (
                    <div className="invitation-view">
                        <div className="welcome-header">
                            <h2>Welcome, {guestData.name}!</h2>
                            <p className="welcome-message">
                                <strong>{guestData.captain_name || 'A Table Captain'}</strong> has invited you to the Raising Girls Luncheon.
                                We would be honored to have you join us.
                            </p>
                        </div>

                        <div className="invitation-actions">
                            <button onClick={showForm} className="accept-btn">Accept Invitation</button>
                            <button onClick={handleDecline} className="decline-btn">Decline</button>
                        </div>
                    </div>
                )}

                {status === 'form' && (
                    <form onSubmit={handleAccept} className="invitation-form">
                        <h3>Complete Your Registration</h3>

                        <div className="form-group">
                            <label>First Name</label>
                            <input
                                type="text"
                                value={formData.firstName}
                                onChange={e => setFormData({ ...formData, firstName: e.target.value })}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Last Name</label>
                            <input
                                type="text"
                                value={formData.lastName}
                                onChange={e => setFormData({ ...formData, lastName: e.target.value })}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Email (Read-only)</label>
                            <input
                                type="email"
                                value={guestData?.email || ''}
                                disabled
                                className="readonly-input"
                            />
                        </div>

                        <div className="form-group">
                            <label>Mailing Address (used for thank you cards)</label>
                            <input
                                type="text"
                                value={formData.address}
                                onChange={e => {
                                    setFormData({ ...formData, address: e.target.value });
                                    setIsAddressValidated(false); // Invalidate on edit
                                }}
                                placeholder="123 Main St, City, State, Zip"
                                required
                            />
                            <p style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '0.5rem', fontStyle: 'italic' }}>
                                Not sure about the exact format? Don't worry, we'll help verify and format it for you.
                            </p>
                        </div>

                        <div className="form-group">
                            <label>Meal Choice</label>
                            <select
                                value={formData.mealChoice}
                                onChange={e => setFormData({ ...formData, mealChoice: e.target.value })}
                            >
                                <option value="Chicken">Chicken</option>
                                <option value="Vegan/Vegetarian">Vegan/Vegetarian</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label>Allergies & Dietary Restrictions</label>
                            <textarea
                                value={formData.allergies}
                                onChange={e => setFormData({ ...formData, allergies: e.target.value })}
                                placeholder="Please let us know of any allergies or dietary restrictions."
                                style={{ minHeight: '80px', fontFamily: 'inherit' }}
                            />
                        </div>

                        {error && <p className="error-text">{error}</p>}

                        <button type="submit" className="submit-button" disabled={loading}>
                            {loading ? 'Submitting...' : 'Confirm Attendance'}
                        </button>
                    </form>
                )}

                {status === 'submitted' && (
                    <div className="success-message">
                        <h3>You're all set!</h3>
                        <p>We can't wait to see you on Wednesday, April 29th!</p>
                        <div className="calendar-links" style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'center', marginTop: '1.5rem', marginBottom: '2rem' }}>
                            <button onClick={handleDownloadCalendar} className="calendar-btn" style={{
                                padding: '0.75rem 1.25rem',
                                background: '#f1f5f9',
                                color: '#334155',
                                border: '1px solid #e2e8f0',
                                borderRadius: '0.75rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                transition: 'all 0.2s'
                            }}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#000' }}>
                                    <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20a2 2 0 0 0 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zm0-12H5V6h14v2z" />
                                </svg>
                                Apple / iCal
                            </button>

                            <a href="https://calendar.google.com/calendar/render?action=TEMPLATE&dates=20260429T183000Z%2F20260429T200000Z&details=Join%20us%20for%20the%203rd%20Annual%20Luncheon%20of%20Raising%20Girls%20on%20April%2029th%20at%20Hotel%20Murano%20Pavilion.&location=Hotel%20Murano%20Pavilion&text=Raising%20Girls%203rd%20Annual%20Luncheon" target="_blank" rel="noopener noreferrer" style={{
                                padding: '0.75rem 1.25rem',
                                background: '#f1f5f9',
                                color: '#334155',
                                border: '1px solid #e2e8f0',
                                borderRadius: '0.75rem',
                                fontWeight: '600',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                textDecoration: 'none',
                                transition: 'all 0.2s'
                            }}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#4285F4' }}>
                                    <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20a2 2 0 0 0 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zm0-12H5V6h14v2z" />
                                    <path d="M12 14h5v2h-5z" />
                                </svg>
                                Google
                            </a>

                            <a href="https://outlook.live.com/calendar/0/action/compose?allday=false&body=Join%20us%20for%20the%203rd%20Annual%20Luncheon%20of%20Raising%20Girls%20on%20April%2029th%20at%20Hotel%20Murano%20Pavilion.&enddt=2026-04-29T13%3A00%3A00&location=Hotel%20Murano%20Pavilion&path=%2Fcalendar%2Faction%2Fcompose&rru=addevent&startdt=2026-04-29T11%3A30%3A00&subject=Raising%20Girls%203rd%20Annual%20Luncheon" target="_blank" rel="noopener noreferrer" style={{
                                padding: '0.75rem 1.25rem',
                                background: '#f1f5f9',
                                color: '#334155',
                                border: '1px solid #e2e8f0',
                                borderRadius: '0.75rem',
                                fontWeight: '600',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                textDecoration: 'none',
                                transition: 'all 0.2s'
                            }}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#0078D4' }}>
                                    <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20a2 2 0 0 0 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zm0-12H5V6h14v2z" />
                                </svg>
                                Outlook
                            </a>

                            <a href="https://outlook.office.com/calendar/0/action/compose?allday=false&body=Join%20us%20for%20the%203rd%20Annual%20Luncheon%20of%20Raising%20Girls%20on%20April%2029th%20at%20Hotel%20Murano%20Pavilion.&enddt=2026-04-29T13%3A00%3A00&location=Hotel%20Murano%20Pavilion&path=%2Fcalendar%2Faction%2Fcompose&rru=addevent&startdt=2026-04-29T11%3A30%3A00&subject=Raising%20Girls%203rd%20Annual%20Luncheon" target="_blank" rel="noopener noreferrer" style={{
                                padding: '0.75rem 1.25rem',
                                background: '#f1f5f9',
                                color: '#334155',
                                border: '1px solid #e2e8f0',
                                borderRadius: '0.75rem',
                                fontWeight: '600',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                textDecoration: 'none',
                                transition: 'all 0.2s'
                            }}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#EA4335' }}>
                                    <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20a2 2 0 0 0 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zm0-12H5V6h14v2z" />
                                </svg>
                                Office 365
                            </a>

                            <a href="https://calendar.yahoo.com/?desc=Join%20us%20for%20the%203rd%20Annual%20Luncheon%20of%20Raising%20Girls%20on%20April%2029th%20at%20Hotel%20Murano%20Pavilion.&dur=false&et=20260429T200000Z&in_loc=Hotel%20Murano%20Pavilion&st=20260429T183000Z&title=Raising%20Girls%203rd%20Annual%20Luncheon&v=60" target="_blank" rel="noopener noreferrer" style={{
                                padding: '0.75rem 1.25rem',
                                background: '#f1f5f9',
                                color: '#334155',
                                border: '1px solid #e2e8f0',
                                borderRadius: '0.75rem',
                                fontWeight: '600',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                textDecoration: 'none',
                                transition: 'all 0.2s'
                            }}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#720E9E' }}>
                                    <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20a2 2 0 0 0 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zm0-12H5V6h14v2z" />
                                    <path d="M12 14h5v2h-5z" />
                                </svg>
                                Yahoo
                            </a>
                        </div>

                        <div className="event-details-card" style={{
                            marginTop: '1rem',
                            padding: '1.5rem',
                            background: '#f8fafc',
                            borderRadius: '0.5rem',
                            textAlign: 'left',
                            border: '1px solid #e2e8f0'
                        }}>
                            <h4 style={{ marginTop: 0, marginBottom: '1rem', color: '#334155' }}>Event Details</h4>
                            <p style={{ margin: '0.5rem 0', color: '#475569' }}><strong>Date:</strong> Wednesday, April 29th, 2026</p>
                            <p style={{ margin: '0.5rem 0', color: '#475569' }}><strong>Time:</strong> 11:30 AM - 1:00 PM</p>
                            <p style={{ margin: '0.5rem 0', color: '#475569' }}><strong>Location:</strong> Hotel Murano Pavilion, Tacoma, WA</p>

                            <div className="apple-warning" style={{
                                marginTop: '1.5rem',
                                padding: '1rem',
                                background: '#fff1f2',
                                borderRadius: '0.375rem',
                                fontSize: '0.9rem',
                                color: '#be123c'
                            }}>
                                <strong>Note for Apple Users:</strong> Due to security restrictions on some iOS devices, the calendar file may not download or open directly. If the button above doesn't work, please use the details above to manually add the event to your calendar.
                            </div>
                        </div>
                    </div>
                )}

                {status === 'declined' && (
                    <div className="success-message declined-view">
                        <h3>We'll see you another time!</h3>
                        <p>We're sad you won't be joining us, but you can still contribute.</p>

                        <div className="declined-actions">
                            <a href="https://bit.ly/rg-luncheon-2026-donate" target="_blank" rel="noreferrer" className="action-btn donate-btn">
                                Donate to the cause
                            </a>
                            <a href="https://raisinggirls.org/newsletter" target="_blank" rel="noreferrer" className="action-btn newsletter-btn">
                                Sign up for our newsletter
                            </a>
                        </div>
                    </div>
                )}
                {/* Decline Confirmation Modal */
                    declineConfirmation && (
                        <div className="validation-overlay">
                            <div className="validation-modal">
                                <h3>Are you sure?</h3>
                                <p>We'll miss you at the luncheon! Please confirm you want to decline this invitation.</p>
                                <div className="validation-actions">
                                    <button className="btn-secondary" onClick={() => setDeclineConfirmation(false)}>Go Back</button>
                                    <button className="btn-primary" style={{ background: '#ef4444', borderColor: '#ef4444' }} onClick={handleConfirmDecline}>Yes, Decline</button>
                                </div>
                            </div>
                        </div>
                    )}

            </div>

            {/* Address Validation Modal */}
            {validationState && (
                <div className="validation-overlay">
                    <div className="validation-modal">
                        {validationState === 'FIX' && (
                            <>
                                <h3>We couldn't find that address</h3>
                                <p>The address you entered looks incomplete or incorrect. Please check for typos.</p>
                                <div className="address-comparison">
                                    <div className="address-box original">
                                        <span className="address-label">You Entered</span>
                                        <div className="address-value">{formData.address}</div>
                                    </div>
                                </div>
                                <div className="validation-actions">
                                    <button className="btn-secondary" onClick={handleValidationKeep}>Use Mine Anyway</button>
                                    <button className="btn-primary" onClick={handleValidationEdit}>Edit Address</button>
                                </div>
                            </>
                        )}

                        {validationState === 'CONFIRM_ADD_SUBPREMISES' && (
                            <>
                                <h3>Missing Unit Number?</h3>
                                <p>It looks like this building requires a unit, apartment, or suite number.</p>
                                <div className="address-comparison">
                                    <div className="address-box suggested">
                                        <span className="address-label">Suggested Format</span>
                                        <div className="address-value">{suggestedAddress} <span style={{ opacity: 0.5 }}>#Unit</span></div>
                                    </div>
                                </div>
                                <div className="validation-actions">
                                    <button className="btn-secondary" onClick={handleValidationConfirm}>No Unit Needed</button>
                                    <button className="btn-primary" onClick={handleValidationEdit}>Add Unit Number</button>
                                </div>
                            </>
                        )}

                        {validationState === 'CONFIRM' && (
                            <>
                                <h3>Did you mean...</h3>
                                <p>We found a match that is slightly different. detailed below.</p>
                                <div className="address-comparison">
                                    <div className="address-box original">
                                        <span className="address-label">You Entered</span>
                                        <div className="address-value">{formData.address}</div>
                                    </div>
                                    <div className="address-box suggested">
                                        <span className="address-label">We Suggest</span>
                                        <div className="address-value">{suggestedAddress}</div>
                                    </div>
                                </div>
                                <div className="validation-actions">
                                    <button className="btn-secondary" onClick={handleValidationKeep}>Keep Original</button>
                                    <button className="btn-primary" onClick={handleValidationConfirm}>Use Suggested</button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}



        </div>
    );
};

export default Invitation;
